import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import SitePreferences from "@/components/SitePreferences";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/zezo764i_Grup_Xaluca_logo.png.webp";

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer
      className="bg-[#1A1513] text-[#FDFBF7] relative overflow-hidden isolate"
      data-testid="site-footer"
    >
      {/* Top hairline — subtle gold rule that reads as the upper edge of
          the footer frame and pairs with the monogram below. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent 0%, rgba(212,163,115,0.0) 8%, rgba(212,163,115,0.55) 50%, rgba(212,163,115,0.0) 92%, transparent 100%)",
        }}
      />

      {/* X monogram tucked into the bottom-right corner as a decorative
          border treatment, matching the site-wide brand language. Lower
          opacity here so it sits politely behind the footer text. */}
      <BrandMonogramEdge
        size="xl"
        opacity={0.22}
        testIdPrefix="footer-brand"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12 relative z-[2]">
        <div className="md:col-span-5">
          {/* Grup Xaluca circular logo + wordmark, prominently placed */}
          <div className="flex items-center gap-4" data-testid="footer-brand-block" data-cms-scope="global.footer">
            <img
              src={LOGO_URL}
              alt="Grup Xaluca"
              className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-1 ring-[#D4A373]/40 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.65)]"
              data-no-xaluca-logo="true" loading="lazy" decoding="async"
              data-cms-role="logo"/>
            <div>
              <div className="font-serif-x text-3xl tracking-tight leading-none">
                Grup Xaluca
              </div>
              <div className="overline text-[#D4A373] mt-2 tracking-[0.3em] text-[10px]">
                The Soul of Southern Morocco
              </div>
            </div>
          </div>

          <p
            className="mt-6 font-serif-x italic text-lg md:text-xl text-[#FDFBF7]/85 max-w-md leading-[1.35] tracking-tight"
            data-testid="footer-tagline"
          >
            Family-Owned. Moroccan at Heart.
          </p>

          {/* Arabic welcome integrated into the brand block — visible on
              all viewports, sits between the tagline and the social
              icons as an elegant signature line. */}
          <p
            lang="ar"
            dir="rtl"
            className="mt-4 inline-flex items-center gap-3 text-[#D4A373] text-base sm:text-lg font-serif-x leading-none"
            data-testid="footer-arabic-welcome"
            aria-label="Welcome in Arabic"
          >
            <span aria-hidden="true" className="inline-block w-6 h-px bg-[#D4A373]/55" />
            <span>مرحبًا بكم</span>
            <span aria-hidden="true" className="inline-block w-6 h-px bg-[#D4A373]/55" />
          </p>

          <div className="mt-8 flex items-center gap-5 text-[#FDFBF7]/70">
            <a
              href="https://www.instagram.com/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#D4A373] transition-colors"
              data-testid="social-instagram"
            >
              <Instagram strokeWidth={1.5} />
            </a>
            <a
              href="https://www.facebook.com/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#D4A373] transition-colors"
              data-testid="social-facebook"
            >
              <Facebook strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="overline text-[#D4A373] mb-5">{t("footer.explore")}</div>
          <ul className="flex flex-col gap-3 text-sm">
            <li>
              <Link to="/hotels" className="hover:text-[#D4A373]" data-testid="footer-hotels">
                {t("nav.hotels")}
              </Link>
            </li>
            <li>
              <Link
                to="/experiences"
                className="hover:text-[#D4A373]"
                data-testid="footer-experiences"
              >
                {t("nav.experiences")}
              </Link>
            </li>
            <li>
              <Link
                to="/gastronomy"
                className="hover:text-[#D4A373]"
                data-testid="footer-gastronomy"
              >
                {t("nav.gastronomy")}
              </Link>
            </li>
            <li>
              <Link to="/events" className="hover:text-[#D4A373]" data-testid="footer-events">
                {t("nav.events")}
              </Link>
            </li>
            <li>
              <Link to="/brochures" className="hover:text-[#D4A373]" data-testid="footer-brochures">
                {t("nav.brochures")}
              </Link>
            </li>
            <li>
              <Link to="/about" className="hover:text-[#D4A373]" data-testid="footer-about">
                {t("nav.about")}
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline text-[#D4A373] mb-5">{t("footer.contact")}</div>
          <ul className="flex flex-col gap-3 text-sm text-[#FDFBF7]/80">
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-1" strokeWidth={1.5} />
              <span>{t("footer.address")}</span>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              <a href="mailto:xaluca@xaluca.com" className="hover:text-[#D4A373]">
                xaluca@xaluca.com
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              <a href="tel:+212000000000" className="hover:text-[#D4A373]">
                +212 (0) 000 000 000
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Language + currency selectors — permanent footer controls.
          Share global state with the in-drawer selectors (and with
          every price/text consumer across the site) so changes here
          propagate instantly everywhere. */}
      <div className="border-t border-[#FDFBF7]/10 relative z-[2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <SitePreferences variant="dark" testIdPrefix="footer-preferences" />
        </div>
      </div>

      <div className="border-t border-[#FDFBF7]/10 relative z-[2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-[#FDFBF7]/50">
          <span>© {new Date().getFullYear()} Grup Xaluca. {t("footer.rights")}</span>
          <span className="tracking-[0.3em] uppercase text-[10px]">
            Sahara · Atlas · Maroc
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
